import { z } from "zod";
import { TObjectBase, TObjectBaseTDate } from "../base";
import {
  TApiInvoiceReceiverMetadata,
  TApiUserMetadata,
} from "../common/metadata";
import { TFirebaseTimestamp } from "../deprecated";
import { TDate } from "../common/date";

type IMetadata = z.infer<typeof TMetadata>;
const TMetadata = z.object({
  user: TApiUserMetadata.optional(),
  createdByUser: TApiUserMetadata.optional(),
  invoiceReceiver: TApiInvoiceReceiverMetadata.optional(),
  taskID: z.string(),
  taskTitle: z.string(),
  taskNumber: z.string(),
  taskArchived: z.boolean().optional(),
  taskStatus: z.number().optional(),
  propertyID: z.string(),
  propertyName: z.string(),
  timeInHours: z.number().optional(), //calculated for timeEntry
  totalCost: z.number().optional(), //calculated for cost
  vatTotalCost: z.number().optional(), //calculated for cost
});

export type ICost = z.infer<typeof TCost>;
export const TCost = z
  .object({
    costType: z.literal("cost"),
    userID: z.string().optional(),
    description: z.string().optional(),
    unitPrice: z.number().optional(),
    unitCount: z.number().optional(),
    vat: z.number().optional(),
    invoicedTo: z
      .object({
        companyID: z.string(),
        invoiceReceiverID: z.string(),
      })
      .optional(),
    position: z.number().optional(), //For sorting and ordering the cost lines
    metadata: TMetadata.optional(),
  })
  .extend(TObjectBaseTDate.shape);

export type ITimeEntry = z.infer<typeof TTimeEntry>;
export const TTimeEntry = z
  .object({
    costType: z.literal("timeEntry"),
    userID: z.string().optional(),
    description: z.string().optional(),
    startDate: TDate,
    endDate: TDate.optional(),
    unitPrice: z.number().optional(),
    position: z.number().optional(), //For sorting and ordering the cost lines
    metadata: TMetadata.optional(),
  })
  .extend(TObjectBaseTDate.shape);

export type ITaskCosts = z.infer<typeof TTaskCosts>;
export const TTaskCosts = TCost.merge(TTimeEntry).merge(
  z.object({
    costType: z.union([z.literal("cost"), z.literal("timeEntry")]), //overwrite costType
    startDate: TDate.optional(),
    endDate: TDate.optional(),
  })
);

/**
 * @deprecated use TCost, TTimeEntry or TTaskCost instead
 */
export const TRawDeviationCost = z
  .object({
    costType: z.union([z.literal("cost"), z.literal("timeEntry")]),
    description: z.string().optional(),
    invoicedTo: z
      .object({
        companyID: z.string(),
        invoiceReceiverID: z.string(),
      })
      .optional(),
    unitCount: z.number().optional(),
    unitPrice: z.number().optional(),
    userID: z.string().optional(),
    vat: z.number().optional(),
    endDate: TFirebaseTimestamp.optional(),
    startDate: TFirebaseTimestamp.optional(),
  })
  .extend(TObjectBase.shape);
/**
 * @deprecated use ICost, ITimeEntry or ITaskCost instead
 */
export type IRawDeviationCost = z.infer<typeof TRawDeviationCost>;
/**
 * @deprecated use ICost, ITimeEntry or ITaskCost instead
 */
export type IApiTaskCost = z.infer<typeof TApiTaskCost>;
/**
 * @deprecated use TCost, TTimeEntry or TTaskCost instead
 */
export const TApiTaskCost = z.object({
  costType: z.union([z.literal("cost"), z.literal("timeEntry")]),
  description: z.string().optional(),
  userID: z.string().optional(),
  user: TApiUserMetadata.optional(),
  createdByUser: TApiUserMetadata.optional(),
  unitCount: z.number().optional(),
  unitPrice: z.number().optional(),
  invoicedTo: TApiInvoiceReceiverMetadata.optional(),
  vat: z.number().optional(),
  endDate: z.string().optional(),
  startDate: z.string().optional(),
  totalHours: z.number().optional(),
  totalCost: z.number().optional(),
  vatTotalCost: z.number().optional(),
  deviationID: z.string(),
  deviationNumber: z.string(),
  propertyID: z.string(),
  propertyName: z.string(),
  title: z.string(),
});
